import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Box, Button, Card, CardContent, CardHeader, Container, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, MenuItem, TextField, Typography
} from "@mui/material";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import BreadCrumbs from "../components/Breadcrumbs";
import {
    getShopStore,
    createServerStoreItem,
    editServerStoreItem,
    getServerStoreItems,
    deleteServerStoreItem
} from "../http/ShopAPI";
import {useParams} from "react-router-dom";
import {SERVERS_ROUTE} from "../utils/consts";

const ShopItems = observer(() => {
    const { serverId } = useParams();
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [shopItems, setShopItems] = useState([]);
    const [globalItems, setGlobalItems] = useState([]);
    const [globalCategories, setGlobalCategories] = useState([]);
    const [filterCategory, setFilterCategory] = useState("");
    const [form, setForm] = useState({
        is_custom: false,
        item_id: null,
        custom_name: "",
        custom_description: "",
        custom_image_url: "",
        command: "",
        price: 0,
        stock: null,
        is_active: true
    });

    const hrefs = [
        { href: SERVERS_ROUTE, name: "Список серверов" },
    ];

    const handleOpen = (item = null) => {
        if (item) {
            setEditing(item);
            setForm({
                is_custom: !item.item_id,
                item_id: item.item_id || null,
                category_id: item.category_id || null,
                custom_name: item.custom_name || "",
                custom_description: item.custom_description || "",
                custom_image_url: item.custom_image_url || "",
                command: item.command || "",
                price: item.price,
                stock: item.stock,
                is_active: item.is_active
            });
        } else {
            setEditing(null);
            setForm({
                is_custom: false,
                item_id: null,
                custom_name: "",
                custom_description: "",
                custom_image_url: "",
                command: "",
                price: 0,
                stock: null,
                is_active: true
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditing(null);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Удалить этот товар?")) {
            try {
                await deleteStoreItem(id);
                await loadStoreData();
            } catch (err) {
                console.error("Ошибка при удалении:", err);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type === "image/png") {
            setForm(prev => ({ ...prev, image_file: file, image_url: "" }));
        }
    };

    const handleSave = async () => {
        const data = { server_id: serverId, ...form };
        try {
            if (editing) {
                await editServerStoreItem(editing.id, data);
            } else {
                await createServerStoreItem(data);
            }
            await loadServerStoreItems();
            handleClose();
        } catch (err) {
            console.error("Ошибка при сохранении:", err);
        }
    };

    const loadGlobalItems = async () => {
        const { items, categories } = await getShopStore();
        setGlobalItems(items);
        setGlobalCategories(categories);
    };

    const loadServerStoreItems = async () => {
        const data = await getServerStoreItems(serverId);
        setShopItems(data);
    };

    useEffect(() => {
        loadGlobalItems();
        loadServerStoreItems();
    }, [serverId]);

    const filteredGlobalItems = globalItems.filter(item =>
        (!filterCategory || item.category_id === parseInt(filterCategory))
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs={hrefs} name="Список товаров магазина" />
            </Box>
            <Box display="flex" justifyContent="space-between" mb={2} mt={3}>
                <Typography variant="h5">Управление товарами</Typography>
                <Button onClick={() => handleOpen()}>
                    <Plus className="mr-1" /> Добавить товар
                </Button>
            </Box>

            <Grid container spacing={2}>
                {shopItems && shopItems.map(item => (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card>
                            <CardHeader
                                title={item.item_id ? `📦 ${item.name}` : `⭐ ${item.custom_name}`}
                                action={
                                    <>
                                        <IconButton onClick={() => handleOpen(item)}><Pencil /></IconButton>
                                        <IconButton onClick={() => handleDelete(item.id)}><Trash2 /></IconButton>
                                    </>
                                }
                            />
                            {item.image_url && (
                                <Box component="img" src={item.image_url} sx={{ maxHeight: 100, objectFit: "contain", width: "100%" }} />
                            )}
                            <CardContent>
                                <Typography variant="body2">Цена: {item.price}</Typography>
                                <Typography variant="body2">{item.item_id ? "Из мастер-каталога" : "Кастомный товар"}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? "Редактировать товар" : "Добавить товар"}</DialogTitle>
                <DialogContent>
                    <TextField
                        select
                        fullWidth
                        label="Тип товара"
                        value={form.is_custom ? "custom" : "global"}
                        onChange={(e) =>
                            setForm(prev => ({
                                ...prev,
                                is_custom: e.target.value === "custom",
                                item_id: null
                            }))
                        }
                        margin="normal"
                    >
                        <MenuItem value="global">Из мастер-каталога</MenuItem>
                        <MenuItem value="custom">Кастомный товар</MenuItem>
                    </TextField>

                    {!form.is_custom ? (
                        <>
                            <TextField
                                select
                                fullWidth
                                label="Категория"
                                value={form.category_id || filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                                margin="normal"
                            >
                                <MenuItem value="">Все категории</MenuItem>
                                {globalCategories.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                fullWidth
                                label="Выбрать товар"
                                value={form.item_id || ""}
                                onChange={(e) => setForm(prev => ({ ...prev, item_id: parseInt(e.target.value) }))}
                                margin="normal"
                            >
                                {filteredGlobalItems.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <img src={item.image_url} alt={item.name} width={30} style={{ marginRight: 8 }} />
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    ) : (
                        <>
                            <TextField label="Название" name="custom_name" fullWidth value={form.custom_name} onChange={handleChange} margin="normal" />
                            <TextField label="Описание" name="custom_description" fullWidth multiline rows={2} value={form.custom_description} onChange={handleChange} margin="normal" />
                            <TextField label="Изображение (URL)" name="custom_image_url" fullWidth value={form.custom_image_url} onChange={handleChange} margin="normal" />
                            <TextField label="Команда" name="command" fullWidth value={form.command} onChange={handleChange} margin="normal" />
                        </>
                    )}

                    <TextField label="Цена" name="price" type="number" fullWidth value={form.price} onChange={handleChange} margin="normal" />
                    <TextField label="Кол-во (stock)" name="stock" type="number" fullWidth value={form.stock || ""} onChange={handleChange} margin="normal" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Отмена</Button>
                    <Button onClick={handleSave}>Сохранить</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
});

export default ShopItems;